import Col from "@visa/vds/col";
import Divider from "@visa/vds/divider";
import Grid from "@visa/vds/grid";
import Row from "@visa/vds/row";
import Td from "@visa/vds/td";
import Tr from "@visa/vds/tr";
import Typography from "@visa/vds/typography";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import PostTypeIcon from "../../../components/library/PostTypeIcon";
import MCSLoading from "../../../components/MCSLoading";
import MCSPagedTable from "../../../components/MCSPagedTable";
import { GlobalAlertContext } from "../../../context/GlobalAlertContext";
import { LibraryAdminApi } from "../../../services/library.admin.service";
import { DateHelper } from "../../../_helpers/DateHelper";
import { FileHelper } from "../../../_helpers/FileHelper";
import LibraryAccessHistorySearchPanel from "./Components/LibraryAccessHistorySearchPanel";
import { useAuthContextUser } from "context/AuthContext";

const LibraryPostAccessHistory = () => {
  const user = useAuthContextUser();
  const canExport = user && (user.isSuperAdmin || user.hasReportingExport)

  const [error, setError] = useState(null);
  const { promiseInProgress } = usePromiseTracker();
  const [tableData, setTableData] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("timestamp");
  const [desc, setDesc] = useState(true);
  const [totalPage, setTotalPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [orgList, setOrgList] = useState([]);

  const pageSize = 10;

  const [filter, setFilter] = useState({
    dateFrom: null,
    dateTo: null,
    libraryPostTitle: null,
    libraryCategoryName: null,
    userId: null,
    organizationId: null,
  });

  const selectCustomStyles = {
    menu: (provided, state) => ({
      ...provided,
      width: "100%",
      color: state.selectProps.menuColor,
      padding: 0,
    }),
    control: (_, { selectProps: { width } }) => ({
      display: "inline-flex",
      width: "100%",
    }),
    container: (styles) => ({ ...styles, width: "100%" }),
    valueContainer: (styles) => ({ ...styles, padding: "6px 8px" }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";

      return { ...provided, opacity, transition };
    },
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected || state.isFocused ? "white" : "black",
      backgroundColor:
        state.isSelected || state.isFocused ? "#003ea9" : "white",
      padding: 10,
    }),
  };

  const colHeaders = [
    { displayName: "Accessed On", name: "timestamp", orderable: true },
    {
      displayName: "Category",
      name: "libraryCategoryName",
      orderable: true,
    },
    {
      displayName: "Display Title",
      name: "libraryPostTitle",
      orderable: true,
    },
    {
      displayName: "Object Type",
      name: "libraryPostType",
      orderable: false,
    },
    { displayName: "User ID", name: "userId", orderable: false },
    {
      displayName: "Organization Name",
      name: "organizationName",
      orderable: false,
    },
    {
      displayName: "BID",
      name: "bid",
      orderable: false,
    },
    {
      displayName: "Classification",
      name: "libraryClassificationName",
      orderable: false,
    },
  ];

  function HandleClickSearch(alertObj) {
      setTableData(null)
      setPage(1);
      fetchData(1, sortBy, desc, filter);
  }

  const HandleClickExport = async (alertObj) => {
    let skipCount = (page - 1) * pageSize;
    trackPromise(
      LibraryAdminApi.exportPostAccessHistory(
        skipCount,
        sortBy,
        desc,
        filter
      ).then((response) => {
        FileHelper.DownloadFile(response);
      })
    );
  };

  function processParams(filter) {
    if (filter === null) return null;
    let process = cloneDeep(filter);
    process.dateFrom =
      process.dateFrom != null
        ? DateHelper.ToUTCDateTime(process.dateFrom)
        : null;
    process.dateTo =
      process.dateTo != null ? DateHelper.ToUTCDateTime(process.dateTo) : null;
    return process;
  }

  const handleSortChange = async (sortBy = null, desc = false) => {
    fetchData(page, sortBy, desc, filter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, sortBy, desc, filter);
  };

  const fetchData = async (page, sortBy, desc, filter) => {
    let processedFilter = processParams(filter);
    let skipCount = (page - 1) * pageSize;
    trackPromise(
      LibraryAdminApi.searchPostAccessHistory(
        skipCount,
        sortBy,
        desc,
        processedFilter
      )
        .then((response) => {
          let result = response.data.result;
          setTableData(result.items);
          setTotalCount(result.totalCount);
          setTotalPage(Math.ceil(result.totalCount / pageSize));
        })
        .catch((error) => setError(error))
    );
  };

  const fetchOrgList = async () => {
    trackPromise(
      LibraryAdminApi.getOrganizationNames()
        .then((response) => {
          let result = response.data.result;
          if (result && result.length == 0)
            result.push({
              name: "- no organization available -",
              value: "",
              disabled: true,
            });
          setOrgList(result);
        })
        .catch((error) => setError(error))
    );
  };

  useEffect(() => {
    fetchOrgList();
  }, []);

  if (error) {
    return <div>Error: {error.message}</div>;
  } else {
    return (
      <GlobalAlertContext.Consumer>
        {(alertObj) => (
          <>
            <Grid>
              <Row>
                <Col>
                  <Typography tag="h1">Library Access History</Typography>
                </Col>
              </Row>
              <LibraryAccessHistorySearchPanel
                orgList={orgList}
                filter={filter}
                setFilter={setFilter}
                selectCustomStyles={selectCustomStyles}
                handleClickSearch={() => HandleClickSearch(alertObj)}
                enableExport={canExport}
                handleClickExport={() => HandleClickExport(alertObj)}
              />
              <Row>
                <Col>
                  <Typography tag="h4" variant="subtitle">
                    Results
                  </Typography>
                  <Divider type="standard" />
                </Col>
              </Row>
              {promiseInProgress && <MCSLoading />}
              {!promiseInProgress && tableData && (
                <>
                  <Row>
                    <Col>
                      <MCSPagedTable
                        size={"compact"}
                        colHeaders={colHeaders}
                        useSearchBar={false}
                        usePagination={true}
                        selectedPage={page}
                        setSelectedPage={handlePageChange}
                        totalPages={totalPage}
                        ordered={sortBy}
                        setOrdered={setSortBy}
                        sortData={handleSortChange}
                        isDsc={desc}
                        setDsc={setDesc}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        tableData={
                          tableData &&
                          tableData.map((row, key) => (
                            <Tr key={key}>
                              <Td>
                                {DateHelper.ToLocaleDateString(row.timestamp)}
                              </Td>
                              <Td>{row.libraryCategoryName}</Td>
                              <Td>{row.libraryPostTitle}</Td>
                              <Td>
                                {" "}
                                <PostTypeIcon
                                  type={row.libraryPostType}
                                ></PostTypeIcon>
                              </Td>
                              <Td>{row.userId}</Td>
                              <Td>{row.organizationName}</Td>
                              <Td>{row.bid}</Td>
                              <Td>{row.libraryClassificationName}</Td>
                            </Tr>
                          ))
                        }
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Grid>
          </>
        )}
      </GlobalAlertContext.Consumer>
    );
  }
};
export default LibraryPostAccessHistory;
